import { motion } from "framer-motion";
import {
  ClipboardList,
  TrendingUp,
  Users,
  GraduationCap,
  ArrowRight,
} from "lucide-react";

const gold = "var(--color-primary)";
const dim = "var(--hp-text-dim)";
const muted = "var(--hp-text-muted)";
const bebas = "'Bebas Neue', sans-serif";
const mono = "'DM Mono', monospace";
const serif = "'Instrument Serif', serif";

const stakeholders = [
  {
    icon: ClipboardList,
    label: "GYM OWNERS",
    sublabel: "THE PROBLEM",
    challenge:
      "Your mats sit empty between classes while rent runs 24/7. Coordinating private lessons over text is chaos — and you're losing thousands every month.",
    solution: "Automated scheduling fills dead hours. Payments process instantly. You earn while you sleep.",
    solutionLabel: "AFTER OPEN MAT",
  },
  {
    icon: Users,
    label: "COACHES",
    sublabel: "THE PROBLEM",
    challenge:
      "You want to teach more privates but chasing students, juggling DMs, and collecting payments eats your energy. Half your bookings fall through.",
    solution: "Students book you directly. No back-and-forth. You show up, teach, and get paid automatically.",
    solutionLabel: "AFTER OPEN MAT",
  },
  {
    icon: GraduationCap,
    label: "STUDENTS",
    sublabel: "THE PROBLEM",
    challenge:
      "You want private lessons but don't know who's available, when they're free, or how to book. Texting your coach feels awkward. So you just don't.",
    solution: "Browse coaches, pick a time, book in one tap. No awkward messages. Train more, faster.",
    solutionLabel: "AFTER OPEN MAT",
  },
  {
    icon: TrendingUp,
    label: "YOUR REVENUE",
    sublabel: "THE PROBLEM",
    challenge:
      "Every empty mat hour, every missed private lesson, every no-show without a cancellation policy — it all adds up to tens of thousands in lost revenue per year.",
    solution: "Cancellation policies enforced automatically. Every session paid upfront. Revenue split handled for you.",
    solutionLabel: "AFTER OPEN MAT",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.15,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export function HeroInfographic() {
  return (
    <div style={{ width: "100%" }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: 64,
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            background: gold,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 24,
          }}
        >
          <span
            style={{
              fontFamily: bebas,
              fontSize: 18,
              color: "var(--hp-gold-dark-text)",
              lineHeight: 0.9,
              textAlign: "center",
              letterSpacing: "0.02em",
            }}
          >
            OPEN
            <br />
            MAT
          </span>
        </div>
        <h2
          style={{
            fontFamily: bebas,
            fontSize: "clamp(2.5rem, 5vw, 4rem)",
            color: "var(--hp-heading-warm)",
            textAlign: "center",
            lineHeight: 0.95,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          ONE PLATFORM.
          <br />
          <span style={{ color: gold }}>EVERYONE WINS.</span>
        </h2>
        <p
          style={{
            fontFamily: serif,
            fontStyle: "italic",
            fontSize: "clamp(1rem, 1.5vw, 1.2rem)",
            color: muted,
            textAlign: "center",
            maxWidth: 480,
            lineHeight: 1.6,
          }}
        >
          Open Mat connects gym owners, coaches, and students — automating the
          scheduling, payments, and policies that hold everyone back.
        </p>
      </motion.div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 1,
          background: "var(--hp-grid-gap-bg)",
          border: "0.5px solid var(--hp-subtle-border)",
        }}
        className="infographic-grid"
      >
        {stakeholders.map((s, i) => (
          <motion.div
            key={s.label}
            custom={i}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            style={{
              background: "var(--hp-card-alt-bg)",
              padding: "clamp(28px, 4vw, 48px)",
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  border: "1px solid var(--hp-gold-icon-border)",
                  background: "var(--hp-gold-icon-bg)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <s.icon
                  style={{
                    width: 22,
                    height: 22,
                    color: gold,
                    strokeWidth: 1.5,
                  }}
                />
              </div>
              <div>
                <div
                  style={{
                    fontFamily: bebas,
                    fontSize: "clamp(1.4rem, 2.2vw, 1.8rem)",
                    color: "var(--hp-heading-warm)",
                    letterSpacing: "0.06em",
                    lineHeight: 1,
                  }}
                >
                  {s.label}
                </div>
              </div>
            </div>

            <div>
              <div
                style={{
                  fontFamily: mono,
                  fontSize: "0.6rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "var(--hp-danger)",
                  marginBottom: 8,
                  fontWeight: 600,
                }}
              >
                {s.sublabel}
              </div>
              <p
                style={{
                  fontFamily: serif,
                  fontStyle: "italic",
                  fontSize: "clamp(0.9rem, 1.3vw, 1.05rem)",
                  color: muted,
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {s.challenge}
              </p>
            </div>

            <div
              style={{
                borderTop: "0.5px solid var(--hp-gold-border-faint)",
                paddingTop: 16,
              }}
            >
              <div
                style={{
                  fontFamily: mono,
                  fontSize: "0.6rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "var(--hp-gold-a60)",
                  marginBottom: 8,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <ArrowRight style={{ width: 10, height: 10 }} />
                {s.solutionLabel}
              </div>
              <p
                style={{
                  fontFamily: serif,
                  fontSize: "clamp(0.9rem, 1.3vw, 1.05rem)",
                  color: gold,
                  lineHeight: 1.6,
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                {s.solution}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
